import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Star, X, Film } from "lucide-react";
import { uploadAdPhoto, setMediaOrder } from "@/lib/api";
import { startUpload } from "@/lib/videoApi";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CardPhoto } from "./PipelineCard";

const MAX_PHOTO_MB = 25;
const isImg = (f) => /^image\//.test(f.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name);
const isVid = (f) => /^video\//.test(f.type) || /\.(mov|mp4|m4v|webm)$/i.test(f.name);
export const MEDIA_ACCEPT = "image/*,video/*,.heic,.heif,.mov,.mp4,.m4v";

const toItems = (files) => [...files].filter((f) => isImg(f) || isVid(f)).map((f) => ({ id: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`, file: f, video: isVid(f), url: URL.createObjectURL(f), pct: 0, state: "ready" }));
const uploadVideo = (horseId, file, onProgress) => new Promise((resolve, reject) => {
  startUpload({ type: "horse", id: horseId }, file, { onProgress, onDone: resolve, onError: (m) => reject(new Error(m)) });
});
// Thumbnail = first image in Ad Order (the Ad Builder's cover). Move the chosen photo to #1.
const coverOrders = (media, attId) => {
  const ordered = media.filter((m) => m.ad_order && m.smartsheet_attachment_id !== attId).sort((a, b) => a.ad_order - b.ad_order);
  return Object.fromEntries([[attId, 1], ...ordered.map((m, i) => [m.smartsheet_attachment_id, i + 2])]);
};

function Tile({ it, cover, onCover, onRemove, busy }) {
  return (
    <div data-testid="add-media-item" className={`relative rounded-lg overflow-hidden border-2 ${cover ? "border-amber-700" : "border-transparent"} bg-stone-100`}>
      {it.video ? <div className="h-24 flex flex-col items-center justify-center text-stone-600 text-xs"><Film className="h-6 w-6" /><span className="px-1 truncate max-w-full">{it.file.name}</span></div>
        : <img src={it.url} alt={it.file.name} className="h-24 w-full object-cover" />}
      {!busy && it.state !== "done" && <button type="button" data-testid="add-media-remove" aria-label={`Remove ${it.file.name}`} onClick={onRemove} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>}
      {!it.video && onCover && <button type="button" data-testid="add-media-set-cover" disabled={busy} onClick={onCover}
        className={`absolute bottom-1 left-1 rounded-full px-2 py-0.5 text-[10px] font-bold inline-flex items-center gap-1 ${cover ? "bg-amber-700 text-white" : "bg-white/90 text-stone-800"}`}><Star className="h-3 w-3" />{cover ? "Thumbnail" : "Set thumbnail"}</button>}
      {it.state !== "ready" && <div data-testid="add-media-progress" className={`absolute inset-x-0 top-0 text-center text-[10px] font-bold ${it.state === "error" ? "bg-red-600 text-white" : "bg-black/60 text-white"}`}>{it.state === "error" ? "Failed" : it.state === "done" ? "Uploaded" : `${it.pct}%`}</div>}
    </div>
  );
}

export function AddMediaModal({ target, onClose, onDone }) {
  const c = target?.c;
  const published = c?.listing_status === "published";
  const [items, setItems] = useState([]);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const more = useRef(null);
  useEffect(() => {
    if (!target) return;
    const its = toItems(target.files || []);
    setItems(its);
    setCover(!c.photo && !published ? its.find((x) => !x.video)?.id || null : null);  // suggest first photo only when the horse has none
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => items.forEach((x) => URL.revokeObjectURL(x.url)), [items]);
  if (!target) return null;
  const add = (files) => setItems((s) => { const n = [...s, ...toItems(files)]; if (!cover && !c.photo && !published) setCover(n.find((x) => !x.video)?.id || null); return n; });
  const upd = (id, f) => setItems((s) => s.map((x) => (x.id === id ? { ...x, ...f } : x)));
  const tooBig = items.filter((x) => !x.video && x.file.size > MAX_PHOTO_MB * 1024 * 1024);
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const hid = c.ids.horse_id;
    let media = null, coverAtt = null, failed = 0;
    for (const it of items) {
      if (it.state === "done") continue;
      upd(it.id, { state: "uploading", pct: 0 });
      try {
        if (it.video) await uploadVideo(hid, it.file, (p) => upd(it.id, { pct: p }));
        else {
          media = await uploadAdPhoto(hid, it.file, (p) => upd(it.id, { pct: p }));
          if (it.id === cover) coverAtt = media[media.length - 1].smartsheet_attachment_id;  // the server appends the new photo last
        }
        upd(it.id, { state: "done", pct: 100 });
      } catch (e) {
        failed += 1;
        upd(it.id, { state: "error" });
        toast.error(`${it.file.name}: ${e?.response?.data?.detail || e.message || "upload failed"}`);
      }
    }
    if (coverAtt && media) {
      try { await setMediaOrder(hid, coverOrders(media, coverAtt)); } catch { toast.error("Photos uploaded, but the thumbnail couldn't be changed."); }
    }
    setBusy(false);
    onDone();
    if (!failed) { toast.success(`${items.length} file${items.length === 1 ? "" : "s"} added to ${c.name}`); onClose(); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent data-testid="add-media-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="add-media-title">Add Media to {c.name}</DialogTitle>
          <DialogDescription>{c.is_test ? "TEST" : "LIVE"} horse · nothing is uploaded until you confirm. Uploading never publishes or changes a live ad.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <CardPhoto c={c} className="h-14 w-16 rounded-lg shrink-0" />
          <div className="text-sm"><div className="font-semibold text-stone-900">{c.name}</div><div data-testid="add-media-count" className="text-stone-600">{items.filter((x) => x.state !== "done").length} file{items.filter((x) => x.state !== "done").length === 1 ? "" : "s"} to add{items.some((x) => x.state === "done") ? ` · ${items.filter((x) => x.state === "done").length} uploaded` : ""}</div></div>
        </div>
        <p data-testid="add-media-cover-note" className="text-xs text-stone-600">
          {published ? "This ad is published: new files are added to the media library only. Change the ad's cover in the Ad Builder."
            : cover ? "The starred photo will become the thumbnail." : c.photo ? "Current thumbnail is kept unless you choose a new one." : "Choose a thumbnail for this horse."}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {items.map((it) => <Tile key={it.id} it={it} busy={busy} cover={cover === it.id} onCover={published ? null : () => setCover(cover === it.id ? null : it.id)}
            onRemove={() => { setItems((s) => s.filter((x) => x.id !== it.id)); if (cover === it.id) setCover(null); }} />)}
          <button type="button" data-testid="add-media-more" disabled={busy} onClick={() => more.current?.click()} className="h-24 rounded-lg border-2 border-dashed border-stone-300 text-stone-600 text-xs inline-flex flex-col items-center justify-center gap-1"><Plus className="h-5 w-5" />Add more</button>
        </div>
        <input ref={more} type="file" multiple accept={MEDIA_ACCEPT} className="hidden" data-testid="add-media-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        {tooBig.length > 0 && <p data-testid="add-media-too-big" className="text-xs font-medium text-red-600">{tooBig.map((x) => x.file.name).join(", ")} {tooBig.length === 1 ? "is" : "are"} larger than {MAX_PHOTO_MB} MB.</p>}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button data-testid="add-media-cancel" variant="outline" disabled={busy} onClick={onClose} className="h-11 rounded-full">Cancel</Button>
          <Button data-testid="add-media-confirm" disabled={busy || !items.length || tooBig.length > 0 || items.every((x) => x.state === "done")} onClick={confirm} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm Upload (${items.filter((x) => x.state !== "done").length})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
